/**
 * brokerTiers.js — the three row-scope tiers an external broker user can be
 * put on (de-hub migration 088, `broker_agent_access.tier`).
 *
 * All three are the SAME Entra role, `BD.BrokerAgent`. The tier is app data on
 * the roster row, not a second app role, because it is useless without the AOI
 * list / roster name that only that row can hold — and de-hub already reads
 * the row on every external request, so it enforces the tier in exactly the
 * place it enforces the brokerage.
 *
 * What the tier does NOT change: which columns a broker may see or write, what
 * they can add or delete, or any endpoint they can reach. Those are the
 * external/internal rules in `permissions.js` and are identical across the
 * three. The tier only narrows WHICH DEALS of their own brokerage they reach:
 *
 *   admin  every deal in the brokerage (the pre-088 behaviour, and the
 *          default for a roster row saved before tiers existed)
 *   lead   only deals in the AOIs assigned to them — which is why AOI is a
 *          required field on broker-submitted deals: a deal with no AOI is
 *          invisible to every lead in the brokerage
 *   agent  only deals whose Agent is them (matched on their roster name,
 *          which is the same value the deal Agent dropdown offers)
 *
 * This is UI vocabulary only — labels for a picker and a grid cell. The server
 * is the boundary.
 */

export const BROKER_TIER_ADMIN = "admin";
export const BROKER_TIER_LEAD = "lead";
export const BROKER_TIER_AGENT = "agent";

/** Picker options, widest scope first. */
export const BROKER_TIER_OPTIONS = [
  {
    value: BROKER_TIER_ADMIN,
    label: "Broker admin — all deals in the brokerage",
  },
  {
    value: BROKER_TIER_LEAD,
    label: "Broker lead — deals in their AOIs",
  },
  {
    value: BROKER_TIER_AGENT,
    label: "Broker agent — deals they are named on",
  },
];

/** Short labels for grid cells / tags. */
export const BROKER_TIER_SHORT_LABELS = {
  [BROKER_TIER_ADMIN]: "Broker admin",
  [BROKER_TIER_LEAD]: "Broker lead",
  [BROKER_TIER_AGENT]: "Broker agent",
};

/**
 * Short label for a stored tier value. An unknown or missing value reads as
 * the admin tier — that is what a pre-088 row means, and what the server
 * defaults such a row to.
 *
 * @param {string|null|undefined} tier
 * @returns {string}
 */
export const brokerTierLabel = (tier) =>
  BROKER_TIER_SHORT_LABELS[tier] ||
  BROKER_TIER_SHORT_LABELS[BROKER_TIER_ADMIN];
